import { useState, useContext } from "react";
import * as Authentication from '@Services/Authentication';
import { ButtonState, InputFieldChangeDelegate } from "@jcharlesworthuk/your-mum-frontend/models";
import * as Sentry from '@sentry/browser';
import { isNullOrEmpty } from "@jcharlesworthuk/your-mum-core/dist/functions";
import { InputChangeDelegate } from "@jcharlesworthuk/your-mum-frontend/helpers/InputDelegates";
import { CurrentUser } from "@Models/CurrentUser";
import { IncomingUserContext, IncomingUserContextActionType } from "@Contexts";

function isValid(inputs: ChangePasswordWithOldPasswordRequest) {
    return !isNullOrEmpty(inputs.newPassword)
        && !isNullOrEmpty(inputs.oldPassword)
        && !isNullOrEmpty(inputs.newPasswordCopy)
        && inputs.newPassword === inputs.newPasswordCopy;
}

export type ChangePasswordWithOldPasswordRequest = {

    oldPassword: string;

    newPassword: string;

    newPasswordCopy: string;
}

export const useChangePasswordForm = (email: string, oldPassword?: string) => {
    const [fields, setFields] = useState<ChangePasswordWithOldPasswordRequest>({
        oldPassword: oldPassword || '',
        newPassword: '',
        newPasswordCopy: ''
    });
    const [buttonState, setButtonState] = useState<ButtonState>(ButtonState.Disabled);
    const [error, setError] = useState<string | undefined>(undefined);
    const [changeSuccess, setChangeSuccess] = useState(false);
    const { dispatch } = useContext(IncomingUserContext);

    const handlePasswordInputChange = function (id: string) {
        const handler: InputChangeDelegate = e => {
            const newWriteModel = Object.assign(Object.create(fields), fields);
            (newWriteModel as any)[id] = e.currentTarget.value;
            setFields(newWriteModel);
            setButtonState(isValid(newWriteModel) ? ButtonState.Enabled : ButtonState.Disabled);
        }
        return handler;
    }

    const handleFormSubmit = (callback?: (user: CurrentUser) => void) => (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const fetchDataAction = async () => {
            setButtonState(ButtonState.Working);
            try {
                let user: CurrentUser | undefined = undefined;
                if (oldPassword) {
                    user = await Authentication.signInAndChangePassword(email, oldPassword, fields.newPassword);
                    if (!user) {
                        throw new Error('No user returned after login & change password!')
                    }
                    dispatch({ type: IncomingUserContextActionType.LoggedIn, user: user });
                } else {
                    console.log('Changing password');
                    await Authentication.changePasswordWithPrevious(email, fields.oldPassword, fields.newPassword);
                }
                setButtonState(ButtonState.Enabled);
                setChangeSuccess(true);
                if (callback) {
                    if (!user) throw new Error('You cant do the callback if you didnt supply an oldPassword prop, as this form will not have logged you in as part of the password change');
                    callback(user);
                }
            } catch (e) {
                console.error(e);
                Sentry.captureException(e);
                if (e.name == 'UsernameExistsException') {
                    setError('exists');
                } else {
                    setError(e.message);
                }
            }

        };
        fetchDataAction();
    }

    return {
        fields,
        handlePasswordInputChange,
        handleFormSubmit,
        changeSuccess,
        error,
        buttonState
    };
}